import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  Container,
  CircularProgress,
  IconButton,
  Alert,
  Stack,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Money as MoneyIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Inbox as InboxIcon,
  WaterDrop as WaterIcon,
  Info as InfoIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import MobileBottomBar from './common/MobileBottomBar';

const CheckUnclaimedChecks = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [checks, setChecks] = useState([]);
  const [filteredChecks, setFilteredChecks] = useState([]);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  // Fetch all ready to claim checks on component mount
  useEffect(() => {
    fetchReadyToClaimChecks();
  }, []);

  // Filter checks when search term changes
  useEffect(() => {
    if (searched) {
      filterChecks();
    }
  }, [searchTerm, checks, searched]);

  const fetchReadyToClaimChecks = async () => {
    setLoading(true);
    setError('');
    setSearched(true);
    
    try {
      const { data, error } = await supabase
        .from('elogbook_transactions')
        .select('*')
        .eq('status', 'ready_to_claim')
        .not('check_number', 'is', null)
        .order('check_created_date', { ascending: false, nullsFirst: false });

      if (error) throw error;

      setChecks(data || []);
      setFilteredChecks(data || []);
    } catch (err) {
      console.error('Error fetching checks:', err);
      setError('Failed to load unclaimed checks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterChecks = () => {
    if (!searchTerm.trim()) {
      setFilteredChecks(checks);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = checks.filter(check => {
      const payee = (check.voucher_payee || check.payee_name || '').toLowerCase();
      const particulars = (check.voucher_particulars || '').toLowerCase();
      const checkNumber = (check.check_number || '').toLowerCase();
      const cafoaNumber = (check.cafoa_number || '').toLowerCase();
      const dvNumber = (check.dv_number || '').toLowerCase();

      return (
        payee.includes(term) ||
        particulars.includes(term) ||
        checkNumber.includes(term) ||
        cafoaNumber.includes(term) ||
        dvNumber.includes(term)
      );
    });

    setFilteredChecks(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    filterChecks();
  };

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#f8faf9', py: { xs: 3, md: 5 }, width: '100%' }}>
      <Container maxWidth="xl">
        {/* Header & Back Navigation */}
        <Box sx={{ mb: 3.5 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/')}
            sx={{ 
              mb: 2,
              color: '#475569',
              fontWeight: 600,
              borderRadius: 2,
              px: 1.5,
              bgcolor: '#ffffff',
              border: '1px solid #e2e8f0',
              '&:hover': { bgcolor: '#f1f5f9' }
            }}
          >
            Back to Home
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <Box 
              sx={{ 
                width: 44, 
                height: 44, 
                borderRadius: 3, 
                bgcolor: 'rgba(217, 119, 6, 0.12)', 
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <MoneyIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#0f172a">
                Check Unclaimed Checks & Vouchers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search released municipal checks ready for claiming at the Municipal Treasury Office.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Search Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2.5, sm: 3 }, 
            mb: 3.5, 
            borderRadius: 4, 
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04)'
          }}
        >
          <form onSubmit={handleSearch}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={9.5}>
                <TextField
                  fullWidth
                  placeholder="Search by Payee / Beneficiary Name, Check No., CAFOA, or particulars..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, bgcolor: '#ffffff' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2.5}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                  sx={{ 
                    height: 48,
                    bgcolor: '#d97706',
                    color: '#ffffff',
                    fontWeight: 700,
                    borderRadius: 2.5,
                    boxShadow: '0 4px 12px rgba(217, 119, 6, 0.25)',
                    '&:hover': { bgcolor: '#b45309' }
                  }}
                >
                  {loading ? 'Searching...' : 'Filter Checks'}
                </Button>
              </Grid>
            </Grid>
          </form>
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2.5 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Results Section */}
        {searched && !loading && (
          <Box sx={{ pb: { xs: 8, md: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon sx={{ color: '#d97706', fontSize: 20 }} />
                Ready to Claim ({filteredChecks.length})
              </Typography>
              <Chip 
                label={`${filteredChecks.length} Available`}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(217, 119, 6, 0.1)', 
                  color: '#d97706', 
                  fontWeight: 700,
                  fontSize: '0.75rem'
                }}
              />
            </Box>

            {filteredChecks.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}>
                <InboxIcon sx={{ fontSize: 56, color: '#94a3b8', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} color="#0f172a">No Unclaimed Checks Found</Typography>
                <Typography variant="body2" color="text.secondary">All checks matching your search have either been claimed or are still in processing.</Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {filteredChecks.map((check) => (
                  <Card 
                    key={check.id} 
                    elevation={0}
                    sx={{ 
                      borderRadius: 3.5, 
                      bgcolor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.08)',
                        borderColor: '#d97706'
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                      <Grid container spacing={2.5} alignItems="center">
                        <Grid item xs={12} md={4.5}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box 
                              sx={{ 
                                width: 38, 
                                height: 38, 
                                borderRadius: 2.5, 
                                bgcolor: 'rgba(217, 119, 6, 0.1)', 
                                color: '#d97706', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.25
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Payee / Beneficiary Name
                              </Typography>
                              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.3 }}>
                                {check.voucher_payee || check.payee_name || '—'}
                              </Typography>
                              {check.check_number && (
                                <Chip 
                                  label={`Check #${check.check_number}`}
                                  size="small"
                                  sx={{ mt: 0.75, bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box 
                              sx={{ 
                                width: 38, 
                                height: 38, 
                                borderRadius: 2.5, 
                                bgcolor: '#f1f5f9', 
                                color: '#64748b', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.25
                              }}
                            >
                              <DescriptionIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Particulars / Purpose
                              </Typography>
                              <Typography variant="body2" color="#334155" sx={{ lineHeight: 1.5, mt: 0.25 }}>
                                {check.voucher_particulars || check.check_remarks || check.voucher_type || 'Disbursement voucher ready for claiming.'}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={2.5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <Chip 
                            label="Ready to Claim"
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(5, 150, 105, 0.12)', 
                              color: '#059669', 
                              fontWeight: 800,
                              fontSize: '0.75rem',
                              border: '1px solid rgba(5, 150, 105, 0.25)',
                              py: 1.75
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            Claim at MTO Treasury Desk
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Container>

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={[
          { name: 'Home', icon: <InfoIcon />, path: '/' },
          { name: 'Water Bills', icon: <WaterIcon />, path: '/check-water-bills' },
          { name: 'Unclaimed Checks', icon: <ReceiptIcon />, path: '/check-unclaimed-checks' },
          { name: 'Staff Portal', icon: <LoginIcon />, path: '/login' }
        ]}
        currentPath="/check-unclaimed-checks"
        onNavigate={(path) => navigate(path)}
      />
    </Box>
  );
};

export default CheckUnclaimedChecks;

